import { useEffect, useState, useRef, useCallback } from "react";

/**
 * Possible states for open/close transitions
 */
export type OpenCloseTransitionStates =
  | "opening"
  | "open"
  | "closing"
  | "closed";

/**
 * Effects configuration for open/close transition states
 * @property {() => void} [onOpenAnimationStart] - Callback when opening animation starts
 * @property {() => void} [onCloseAnimationStart] - Callback when closing animation starts
 * @property {() => void} [onOpenAnimationComplete] - Callback when opening animation completes
 * @property {() => void} [onCloseAnimationComplete] - Callback when closing animation completes
 * @property {number | (() => number)} [durationMs] - Duration of the animation in milliseconds (or a function that returns the duration)
 */
export type OpenCloseTransitionStateEffects = {
  /**
   * Callback when opening animation starts
   */
  onOpenAnimationStart?: () => void;
  /**
   * Callback when closing animation starts
   */
  onCloseAnimationStart?: () => void;
  /**
   * Callback when opening animation completes
   */
  onOpenAnimationComplete?: () => void;
  /**
   * Callback when closing animation completes
   */
  onCloseAnimationComplete?: () => void;
};

export type OpenCloseTransitionState = {
  state: OpenCloseTransitionStates;
  beginClosing: () => void;
  isOpenOrOpening: boolean;
  isClosedOrClosing: boolean;
};

export type OpenCloseTransitionStateOptions = {
  durationMs: number | (() => number);
  onOpenAnimationStart?: () => void;
  onCloseAnimationStart?: () => void;
  onOpenAnimationComplete?: () => void;
  onCloseAnimationComplete?: () => void;
};

/**
 * Custom hook for managing open/close transition states and animations.
 *
 * @param openProp - The open prop controlling visibility
 * @param options - Additional, optional options including callbacks for animation start, complete, and initialization
 * @returns Object containing state, control functions, and utility flags
 */
export const useOpenCloseTransitionStates = (
  openProp: boolean | undefined,
  options: OpenCloseTransitionStateOptions = {
    durationMs: 0,
  },
): OpenCloseTransitionState => {
  const {
    onOpenAnimationStart,
    onCloseAnimationStart,
    onOpenAnimationComplete,
    onCloseAnimationComplete,
    durationMs,
  } = options;
  const prevOpenProp = useRef<boolean | undefined>(openProp);
  const [state, setState] = useState<OpenCloseTransitionStates>(
    openProp ? "open" : "closed",
  );

  // Handle open/close prop changes
  useEffect(() => {
    if (prevOpenProp.current && !openProp) {
      // previously open, now closed
      setState("closing");
    } else if (!prevOpenProp.current && openProp) {
      // previously closed, now open
      setState("opening");
    } else {
      // Note: Using "opening" here catches the case where the element is initially open, allowing callbacks and animations to run.
      setState(openProp ? "opening" : "closed");
    }
    prevOpenProp.current = openProp;
  }, [openProp]);

  // Handle opening/closing animations
  useEffect(() => {
    if (!durationMs) return;
    if (state === "open" || state === "closed") return;
    if (state === "opening") {
      onOpenAnimationStart?.();
    } else if (state === "closing") {
      onCloseAnimationStart?.();
    }
    const d: number =
      typeof durationMs === "function" ? durationMs() : durationMs;
    const timeout = setTimeout(() => {
      if (state === "opening") {
        setState("open");
        onOpenAnimationComplete?.();
      } else if (state === "closing") {
        setState("closed");
        onCloseAnimationComplete?.();
      }
    }, d);

    return () => {
      clearTimeout(timeout);
    };
  }, [
    state,
    onOpenAnimationComplete,
    onCloseAnimationComplete,
    onOpenAnimationStart,
    onCloseAnimationStart,
    durationMs,
  ]);

  const beginClosing = useCallback(() => {
    setState("closing");
  }, []);

  return {
    state,
    beginClosing,
    isOpenOrOpening: state === "open" || state === "opening",
    isClosedOrClosing: state === "closed" || state === "closing",
  };
};
